import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useContent } from '@/context/ContentContext';
import type { BundleRow } from '@/types/bundle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, X, GripVertical } from 'lucide-react';

type Draft = Partial<BundleRow> & { product_handles: string[] };

const emptyDraft: Draft = {
  slug: '',
  title_he: '',
  subtitle_he: '',
  tagline_he: '',
  description_he: '',
  price_ils: null,
  compare_at_ils: null,
  discount_pct: 12,
  image_url: '',
  video_url: '',
  product_handles: [],
  position: 0,
  is_active: true,
  discount_code: '',
};


async function uploadMedia(file: File, kind: 'image' | 'video'): Promise<string | null> {
  const ext = file.name.split('.').pop() || (kind === 'video' ? 'mp4' : 'jpg');
  const path = `bundles/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from('riham-media').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (error) {
    toast.error('שגיאה בהעלאת קובץ', { description: error.message });
    return null;
  }
  const { data } = supabase.storage.from('riham-media').getPublicUrl(path);
  return data.publicUrl;
}

function MediaPicker({
  label,
  value,
  onChange,
  kind,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  kind: 'image' | 'video';
}) {
  const [uploading, setUploading] = useState(false);
  const accept = kind === 'image' ? 'image/*' : 'video/*';
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="flex-1"
        />
        <label
          className={`inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md cursor-pointer bg-white hover:bg-neutral-50 ${
            uploading ? 'opacity-60 pointer-events-none' : ''
          }`}
        >
          <Upload className="h-4 w-4" />
          {uploading ? 'מעלה…' : 'העלאה'}
          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploading(true);
              const url = await uploadMedia(file, kind);
              setUploading(false);
              if (url) onChange(url);
              e.target.value = '';
            }}
          />
        </label>
        {value && (
          <Button variant="ghost" size="icon" onClick={() => onChange('')} title="ניקוי">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {value && kind === 'image' && (
        <img src={value} alt="" className="h-32 rounded-md object-cover border" />
      )}
      {value && kind === 'video' && (
        <video src={value} className="h-32 rounded-md border" controls muted />
      )}
    </div>
  );
}

function ProductPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const { products } = useContent();
  const add = (id: string) => {
    if (value.includes(id)) return;
    onChange([...value, id]);
  };
  const remove = (id: string) => onChange(value.filter((v) => v !== id));
  const move = (id: string, dir: -1 | 1) => {
    const idx = value.indexOf(id);
    const next = [...value];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <Label>מוצרים במארז</Label>
      <div className="border rounded-md divide-y bg-white">
        {value.length === 0 && (
          <div className="px-3 py-2 text-sm text-muted-foreground">לא נבחרו מוצרים</div>
        )}
        {value.map((id) => {
          const p = products.find((x) => x.id === id);
          return (
            <div key={id} className="flex items-center gap-2 px-2 py-2">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 text-sm">
                {p ? p.name : <span className="text-red-600">{id} (לא נמצא)</span>}
                <span className="text-muted-foreground"> · {id}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => move(id, -1)}>↑</Button>
              <Button variant="ghost" size="sm" onClick={() => move(id, 1)}>↓</Button>
              <Button variant="ghost" size="icon" onClick={() => remove(id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        {products
          .filter((p) => !value.includes(p.id))
          .map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => add(p.id)}
              className="text-xs px-2 py-1 border rounded-md bg-white hover:bg-neutral-50"
            >
              <Plus className="inline h-3 w-3 me-1" />
              {p.name}
            </button>
          ))}
      </div>
    </div>
  );
}

function BundleEditor({
  row,
  onSaved,
  onCancel,
  onDelete,
}: {
  row: Draft;
  onSaved: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(row);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.title_he || !draft.slug) {
      toast.error('שדות חובה: שם וזיהוי (slug)');
      return;
    }
    setSaving(true);
    const payload = {
      slug: draft.slug,
      title_he: draft.title_he,
      subtitle_he: draft.subtitle_he ?? null,
      tagline_he: draft.tagline_he ?? null,
      description_he: draft.description_he ?? null,
      price_ils: draft.price_ils ?? null,
      compare_at_ils: draft.compare_at_ils ?? null,
      discount_pct: draft.discount_pct ?? null,
      image_url: draft.image_url || null,
      video_url: draft.video_url || null,
      product_handles: draft.product_handles ?? [],
      position: draft.position ?? 0,
      is_active: draft.is_active ?? true,
      discount_code: draft.discount_code?.trim() ? draft.discount_code.trim() : null,
    };

    const { error } = draft.id
      ? await supabase.from('bundles').update(payload).eq('id', draft.id)
      : await supabase.from('bundles').insert(payload);
    setSaving(false);
    if (error) {
      toast.error('שגיאה בשמירה', { description: error.message });
      return;
    }
    toast.success('נשמר');
    onSaved();
  };

  // Live price preview (mirrors Shop.tsx BundleCard logic)
  const { products } = useContent();
  const previewItems = (draft.product_handles ?? [])
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean) as Array<{ id: string; name: string; price: string | number }>;
  const priceNum = (v: string | number) =>
    typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.]/g, '')) || 0;
  const originalSum = previewItems.reduce((s, p) => s + priceNum(p.price), 0);
  const discountFrac = (draft.discount_pct ?? 0) / 100;
  const previewTotal =
    draft.price_ils != null
      ? Math.round(draft.price_ils)
      : Math.round(originalSum * (1 - discountFrac));
  const previewCompareAt =
    draft.compare_at_ils != null ? Math.round(draft.compare_at_ils) : originalSum;
  const previewDiscountPct =
    draft.price_ils != null && previewCompareAt > 0
      ? Math.round((1 - previewTotal / previewCompareAt) * 100)
      : Math.round((draft.discount_pct ?? 0));

  return (
    <Card className="p-5 space-y-5">
      {/* === מדיה — מוצג ראשון בכרטיס באתר === */}
      <section className="space-y-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
          מדיה (תמונה / וידאו של המארז)
        </div>
        <MediaPicker
          label="תמונה ראשית"
          value={draft.image_url ?? ''}
          onChange={(v) => set('image_url', v)}
          kind="image"
        />
        <MediaPicker
          label="וידאו (אופציונלי — גובר על התמונה)"
          value={draft.video_url ?? ''}
          onChange={(v) => set('video_url', v)}
          kind="video"
        />
        <p className="text-xs text-muted-foreground">
          אם לא הועלו תמונה ולא וידאו — האתר יציג רשת אוטומטית מתמונות המוצרים שבמארז.
        </p>
      </section>

      {/* === טקסטים — בסדר שמופיע בכרטיס === */}
      <section className="space-y-3 pt-3 border-t">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
          טקסטים (בסדר ההופעה בכרטיס)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>תווית עליונה <span className="text-muted-foreground font-normal">(כיתוב קטן UPPERCASE מעל השם)</span></Label>
            <Input
              value={draft.subtitle_he ?? ''}
              onChange={(e) => set('subtitle_he', e.target.value)}
              placeholder="לדוגמה: SIGNATURE SET"
            />
          </div>
          <div>
            <Label>שם המארז <span className="text-red-600">*</span> <span className="text-muted-foreground font-normal">(כותרת ראשית)</span></Label>
            <Input value={draft.title_he ?? ''} onChange={(e) => set('title_he', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>סלוגן <span className="text-muted-foreground font-normal">(שורה אחת בכתב נטוי מתחת לשם)</span></Label>
            <Input value={draft.tagline_he ?? ''} onChange={(e) => set('tagline_he', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>תיאור <span className="text-muted-foreground font-normal">(פסקה קצרה)</span></Label>
            <Textarea
              rows={4}
              value={draft.description_he ?? ''}
              onChange={(e) => set('description_he', e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* === מוצרים === */}
      <section className="space-y-3 pt-3 border-t">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
          מוצרים במארז (רשימה מתחת לתיאור)
        </div>
        <ProductPicker
          value={draft.product_handles}
          onChange={(v) => set('product_handles', v)}
        />
      </section>

      {/* === תמחור === */}
      <section className="space-y-3 pt-3 border-t">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
          תמחור
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          השאר את <b>מחיר סופי</b> ריק כדי לחשב אוטומטית: <code>סכום מוצרים × (1 − אחוז הנחה)</code>.
          <br />השאר את <b>מחיר ללא הנחה</b> ריק כדי להציג את סכום המוצרים המקורי כקו חוצה.
          <br />אם הוזן <b>מחיר סופי</b> — אחוז ההנחה המוצג מחושב מההפרש מול "מחיר ללא הנחה".
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>אחוז הנחה</Label>
            <Input
              type="number"
              value={draft.discount_pct ?? ''}
              onChange={(e) =>
                set('discount_pct', e.target.value === '' ? null : Number(e.target.value))
              }
              placeholder="לדוגמה 12"
            />
          </div>
          <div>
            <Label>מחיר סופי (₪) — אופציונלי</Label>
            <Input
              type="number"
              value={draft.price_ils ?? ''}
              onChange={(e) => set('price_ils', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="ריק = חישוב אוטומטי"
            />
          </div>
          <div>
            <Label>מחיר ללא הנחה (₪) — אופציונלי</Label>
            <Input
              type="number"
              value={draft.compare_at_ils ?? ''}
              onChange={(e) =>
                set('compare_at_ils', e.target.value === '' ? null : Number(e.target.value))
              }
              placeholder={`ריק = ${originalSum || '…'}`}
            />
          </div>
        </div>
        {/* Live preview */}
        <div className="rounded-md border bg-neutral-50 p-3 text-sm flex flex-wrap items-baseline gap-3">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">תצוגה מקדימה:</span>
          <span className="font-semibold text-xl tabular-nums">₪{previewTotal}</span>
          {previewCompareAt > previewTotal && (
            <span className="line-through text-muted-foreground">₪{previewCompareAt}</span>
          )}
          {previewDiscountPct > 0 && (
            <span className="px-2 py-0.5 bg-[#fbbf25] text-[#412018] text-[10px] font-bold tracking-[0.2em] uppercase">
              חיסכון {previewDiscountPct}%
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            (סכום מוצרים: ₪{originalSum || 0})
          </span>
        </div>
      </section>

      {/* === קוד הנחה === */}
      <section className="space-y-3 pt-3 border-t">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
          קוד הנחה של Shopify
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          צור/צרי בקוד הניהול של Shopify (<b>Discounts → Create discount</b>) קוד עם הנחה קבועה בסכום או ב־%
          שיוריד את סך רכיבי המארז בדיוק לערך <b>מחיר סופי</b> למעלה. הדבק/י כאן את הקוד — האתר יחיל אותו על העגלה
          אוטומטית כאשר הלקוח מוסיף את המארז, וירוקן אותו בהסרת המארז.
        </p>
        <div className="max-w-sm">
          <Label>קוד</Label>
          <Input
            value={draft.discount_code ?? ''}
            onChange={(e) => set('discount_code', e.target.value)}
            placeholder="BUNDLE-SKIN-GLOW"
            dir="ltr"
          />
        </div>
      </section>



      {/* === הגדרות === */}
      <section className="space-y-3 pt-3 border-t">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
          הגדרות
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>זיהוי (slug) <span className="text-red-600">*</span></Label>
            <Input
              value={draft.slug ?? ''}
              onChange={(e) => set('slug', e.target.value.replace(/\s+/g, '-').toLowerCase())}
              placeholder="hair-growth-set"
              dir="ltr"
            />
            <p className="text-xs text-muted-foreground mt-1">משמש פנימית, לא מוצג ללקוח.</p>
          </div>
          <div>
            <Label>סדר תצוגה</Label>
            <Input
              type="number"
              value={draft.position ?? 0}
              onChange={(e) => set('position', Number(e.target.value))}
            />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch
              checked={!!draft.is_active}
              onCheckedChange={(v) => set('is_active', v)}
            />
            <Label>פעיל (מוצג באתר)</Label>
          </div>
        </div>
      </section>

      <div className="flex items-center justify-between gap-2 pt-3 border-t">
        <div>
          {onDelete && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700"
              onClick={async () => {
                if (!confirm('למחוק את המארז?')) return;
                onDelete();
              }}
            >
              <Trash2 className="h-4 w-4 me-1" /> מחיקה
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>סגירה</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמירה'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function AdminBundles() {
  const [rows, setRows] = useState<BundleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const { reloadBundles } = useContent();

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('bundles')
      .select('*')
      .order('position', { ascending: true });
    setLoading(false);
    if (error) {
      toast.error('שגיאה בטעינה', { description: error.message });
      return;
    }
    setRows((data as unknown as BundleRow[]) ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const handleSaved = async () => {
    setEditingId(null);
    await load();
    await reloadBundles();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('bundles').delete().eq('id', id);
    if (error) {
      toast.error('שגיאה במחיקה', { description: error.message });
      return;
    }
    toast.success('נמחק');
    setEditingId(null);
    await load();
    await reloadBundles();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">מארזים</h1>
          <p className="text-sm text-muted-foreground">
            ניהול מארזי מוצרים — שם, מחיר, מדיה, רשימת מוצרים. השינויים מופיעים מיד באתר.
          </p>
        </div>
        <Button onClick={() => setEditingId('new')}>
          <Plus className="h-4 w-4 me-1" /> מארז חדש
        </Button>
      </div>

      {editingId === 'new' && (
        <BundleEditor
          row={{ ...emptyDraft, position: (rows.at(-1)?.position ?? 0) + 1 }}
          onSaved={handleSaved}
          onCancel={() => setEditingId(null)}
        />
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">טוען…</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const isEditing = editingId === r.id;
            if (isEditing) {
              return (
                <BundleEditor
                  key={r.id}
                  row={{ ...r, product_handles: r.product_handles ?? [] }}
                  onSaved={handleSaved}
                  onCancel={() => setEditingId(null)}
                  onDelete={() => handleDelete(r.id)}
                />
              );
            }
            return (
              <Card key={r.id} className="p-4 flex items-center gap-4">
                <div className="w-20 h-20 bg-neutral-100 rounded overflow-hidden shrink-0">
                  {r.image_url ? (
                    <img src={r.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                      ללא תמונה
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium truncate">{r.title_he}</div>
                    {!r.is_active && <Badge variant="secondary">לא פעיל</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {r.slug} · {r.product_handles?.length ?? 0} מוצרים
                    {r.price_ils != null && ` · ₪${r.price_ils}`}
                    {r.discount_pct != null && ` · ${r.discount_pct}%`}
                  </div>
                </div>
                <Button variant="outline" onClick={() => setEditingId(r.id)}>
                  עריכה
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
